/**
 * WeHonor Sync Engine
 *
 * Compares an existing Memorial + its Events against a newly scanned ImportCandidate.
 * Returns a structured diff that the app can apply and surface to staff.
 */

import { Event, ImportCandidate, Memorial, SyncChange } from "../types/domain";

export interface SyncResult {
  hasChanges: boolean;
  memorialUpdates: Partial<Memorial>;
  newEvents: ImportCandidate["services"];
  updatedEvents: Array<{ eventId: string; updates: Partial<Event> }>;
  removedEventIds: string[];
  changeLog: SyncChange[];
}

/** Normalize a "HH:MM" or "HH:MM:SS" time string into an ISO-style comparable */
function serviceToStartAt(date: string, time: string): string {
  return `${date}T${time.padEnd(5, "0")}:00.000Z`;
}

/** Match an existing Event to a service entry by type + approximate date */
function findMatchingEvent(
  existing: Event[],
  svc: ImportCandidate["services"][number],
): Event | undefined {
  const candidateStart = serviceToStartAt(svc.date, svc.time);
  // First try: exact type + same date
  return (
    existing.find(
      (e) =>
        e.type === svc.type &&
        e.startAt.slice(0, 10) === svc.date,
    ) ??
    // Second try: same date regardless of type (handles renames like "visitation" → "wake")
    existing.find((e) => e.startAt.slice(0, 10) === svc.date) ??
    // Third try: closest start time within 2 hours
    existing
      .map((e) => ({
        event: e,
        delta: Math.abs(
          new Date(e.startAt).getTime() - new Date(candidateStart).getTime(),
        ),
      }))
      .filter((x) => x.delta < 2 * 60 * 60 * 1000)
      .sort((a, b) => a.delta - b.delta)[0]?.event
  );
}

function compareField(
  field: string,
  oldVal: string | undefined,
  newVal: string | undefined,
  changes: SyncChange[],
  label: string,
): void {
  if (!newVal || oldVal === newVal) return;
  changes.push({
    type: "updated",
    entity: "memorial",
    field,
    oldValue: oldVal ?? "(none)",
    newValue: newVal,
    description: `${label} changed`,
  });
}

function compareEventField(
  field: string,
  oldVal: string | undefined,
  newVal: string | undefined,
  changes: SyncChange[],
  label: string,
): void {
  if (!newVal || oldVal === newVal) return;
  changes.push({
    type: "updated",
    entity: "event",
    field,
    oldValue: oldVal ?? "(none)",
    newValue: newVal,
    description: `Event ${label} updated`,
  });
}

/**
 * Diff an existing memorial against a freshly scanned candidate.
 * Returns structured changes that can be applied via `applySyncResult`.
 */
export function diffMemorial(
  memorial: Memorial,
  existingEvents: Event[],
  candidate: ImportCandidate,
): SyncResult {
  const changeLog: SyncChange[] = [];
  const memorialUpdates: Partial<Memorial> = {};
  const updatedEvents: SyncResult["updatedEvents"] = [];
  const newEvents: ImportCandidate["services"] = [];
  const matchedEventIds = new Set<string>();

  // --- Memorial-level field diffs ---
  if (candidate.photoUrl && candidate.photoUrl !== memorial.photoUrl) {
    memorialUpdates.photoUrl = candidate.photoUrl;
    compareField("photoUrl", memorial.photoUrl, candidate.photoUrl, changeLog, "Profile photo");
  }
  if (candidate.obituaryUrl && candidate.obituaryUrl !== memorial.obituaryUrl) {
    memorialUpdates.obituaryUrl = candidate.obituaryUrl;
    compareField("obituaryUrl", memorial.obituaryUrl, candidate.obituaryUrl, changeLog, "Obituary URL");
  }
  if (candidate.productLinks && JSON.stringify(candidate.productLinks) !== JSON.stringify(memorial.productLinks)) {
    memorialUpdates.productLinks = candidate.productLinks;
    changeLog.push({
      type: "updated",
      entity: "memorial",
      field: "productLinks",
      description: "Sympathy/product links refreshed",
    });
  }

  // --- Event-level diffs ---
  for (const svc of candidate.services) {
    const match = findMatchingEvent(existingEvents, svc);

    if (!match) {
      // New event not in the existing set
      newEvents.push(svc);
      changeLog.push({
        type: "created",
        entity: "event",
        description: `New event added: ${svc.title} on ${svc.date}`,
      });
      continue;
    }

    matchedEventIds.add(match.id);
    const eventUpdates: Partial<Event> = {};
    const eventChanges: SyncChange[] = [];

    const candidateStart = serviceToStartAt(svc.date, svc.time);
    const candidateEnd = svc.endTime
      ? serviceToStartAt(svc.date, svc.endTime)
      : candidateStart;

    compareEventField("title", match.title, svc.title, eventChanges, "title");
    compareEventField("startAt", match.startAt, candidateStart, eventChanges, "start time");
    compareEventField("endAt", match.endAt, candidateEnd, eventChanges, "end time");
    compareEventField("venue", match.venue, svc.venue, eventChanges, "venue");
    compareEventField("address", match.address, svc.address, eventChanges, "address");
    compareEventField("livestreamUrl", match.livestreamUrl, svc.livestreamUrl, eventChanges, "livestream URL");

    if (svc.title && svc.title !== match.title) eventUpdates.title = svc.title;
    if (candidateStart !== match.startAt) eventUpdates.startAt = candidateStart;
    if (candidateEnd !== match.endAt) eventUpdates.endAt = candidateEnd;
    if (svc.venue !== match.venue) eventUpdates.venue = svc.venue;
    if (svc.address !== match.address) eventUpdates.address = svc.address;
    if (svc.livestreamUrl !== match.livestreamUrl) eventUpdates.livestreamUrl = svc.livestreamUrl;

    if (eventChanges.length > 0) {
      updatedEvents.push({ eventId: match.id, updates: eventUpdates });
      changeLog.push(...eventChanges);
    }
  }

  // Events in our system but no longer in the candidate scan
  const removedEventIds = existingEvents
    .filter((e) => !matchedEventIds.has(e.id) && e.status !== "completed" && e.status !== "archived")
    .map((e) => {
      changeLog.push({
        type: "removed",
        entity: "event",
        description: `Event removed from website: ${e.title}`,
      });
      return e.id;
    });

  const hasChanges =
    Object.keys(memorialUpdates).length > 0 ||
    updatedEvents.length > 0 ||
    newEvents.length > 0 ||
    removedEventIds.length > 0;

  return {
    hasChanges,
    memorialUpdates,
    newEvents,
    updatedEvents,
    removedEventIds,
    changeLog,
  };
}

/**
 * Apply a SyncResult to in-place data (returns new arrays ready for setData).
 * `memorialId` is the id of the memorial being updated.
 * `makeEventId` is a factory for generating new event IDs.
 */
export function applySyncResult(
  memorialId: string,
  result: SyncResult,
  memorials: Memorial[],
  events: Event[],
  makeEventId: (svc: ImportCandidate["services"][number], index: number) => string,
  now: string,
): { memorials: Memorial[]; events: Event[] } {
  if (!result.hasChanges) return { memorials, events };

  // Apply memorial field updates
  const nextMemorials = memorials.map((m) =>
    m.id === memorialId
      ? {
          ...m,
          ...result.memorialUpdates,
          lastSyncAt: now,
          syncCount: (m.syncCount ?? 1) + 1,
          syncStatus: "synced" as const,
        }
      : m,
  );

  // Apply event updates, remove cancelled events, add new events
  let nextEvents = events
    .filter((e) => !result.removedEventIds.includes(e.id))
    .map((e) => {
      const update = result.updatedEvents.find((u) => u.eventId === e.id);
      return update ? { ...e, ...update.updates } : e;
    });

  // Add new events
  const newEventObjects: Event[] = result.newEvents.map((svc, i) => ({
    id: makeEventId(svc, i),
    memorialId,
    type: svc.type,
    title: svc.title,
    startAt: `${svc.date}T${svc.time}:00.000Z`,
    endAt: svc.endTime
      ? `${svc.date}T${svc.endTime}:00.000Z`
      : `${svc.date}T${svc.time}:00.000Z`,
    venue: svc.venue,
    address: svc.address,
    livestreamUrl: svc.livestreamUrl,
    visibility: "public" as const,
    capacity: 200,
    status: "scheduled" as const,
    rsvpEnabled: true,
    checkInEnabled: true,
    qrMode: "combined" as const,
    confirmationHeadline: "We will hold space for you.",
    confirmationBody: "Your RSVP has been recorded.",
  }));

  nextEvents = [...newEventObjects, ...nextEvents];

  return { memorials: nextMemorials, events: nextEvents };
}
